<template>
  <div>
    <b-loading :is-full-page="true" :active="!initialized"></b-loading>
    <section v-if="initialized" class="section is-main-section">
      <CardComponent>
        <div class="section has-text-centered">
          <h1 class="title">
            {{ added ? 'Create New Channel' : 'Edit Channel' }}
          </h1>
        </div>
        <div class="columns is-vcentered">
          <div class="column is-8 is-offset-2">
            <div class="flex-card light-bordered light-raised">
              <div class="card-body">
                <ValidationObserver ref="observer" v-slot="{ passes, errors }">
                  <form @submit.prevent="passes(submit)">
                    <div class="columns is-multiline">
                      <div class="column is-6">
                        <div class="control">
                          <ValidationProvider
                            v-slot="{ errors }"
                            name="Name"
                            rules="required"
                          >
                            <b-field
                              label="Name"
                              :type="{ 'is-danger': errors.length > 0 }"
                              :message="errors[0]"
                            >
                              <b-input v-model="item.name" />
                            </b-field>
                          </ValidationProvider>
                        </div>
                      </div>
                      <div class="column is-6">
                        <div class="control">
                          <BaseTimePicker
                            v-model="item.start_time"
                            label="Channel Start Time"
                            :increment-minutes="15"
                            rules="required"
                          />
                        </div>
                      </div>
                      <div class="column is-6">
                        <div class="control">
                          <ValidationProvider
                            v-slot="{ errors }"
                            name="Timezone"
                            rules="required"
                          >
                            <b-field
                              label="Timezone"
                              :type="{ 'is-danger': errors.length > 0 }"
                              :message="errors[0]"
                            >
                              <b-select v-model="item.timezone" expanded>
                                <option value="Africa/Johannesburg"
                                  >Africa/Johannesburg (UTC+2)</option
                                >
                                <option value="Africa/Cairo"
                                  >Africa/Cairo (UTC+2)</option
                                >
                                <option value="Africa/Nairobi"
                                  >Africa/Nairobi (UTC+3)</option
                                >
                                <option value="Africa/Lagos"
                                  >Africa/Lagos (UTC+1)</option
                                >
                                <option value="Europe/London"
                                  >Europe/London (UTC+0)</option
                                >
                                <option value="Europe/Paris"
                                  >Europe/Paris (UTC+1)</option
                                >
                                <option value="America/New_York"
                                  >America/New_York (UTC-5)</option
                                >
                                <option value="America/Los_Angeles"
                                  >America/Los_Angeles (UTC-8)</option
                                >
                                <option value="Asia/Dubai"
                                  >Asia/Dubai (UTC+4)</option
                                >
                                <option value="UTC">UTC (UTC+0)</option>
                              </b-select>
                            </b-field>
                          </ValidationProvider>
                        </div>
                      </div>

                      <!-- EPG Configuration -->
                      <div class="column is-12">
                        <hr
                          style="margin: 20px 0; border-top: 2px solid #dbdbdb;"
                        />
                        <h3 class="subtitle is-5" style="margin-bottom: 15px;">
                          <span class="icon">
                            <i class="mdi mdi-television-guide"></i>
                          </span>
                          EPG Configuration
                        </h3>
                      </div>

                      <div class="column is-12">
                        <div class="control">
                          <b-field label="Default Country Blockouts">
                            <b-taginput
                              v-model="item.epg_default_blockouts"
                              :data="filteredBlockoutCountries"
                              autocomplete
                              :allow-new="false"
                              :open-on-focus="true"
                              field="name"
                              icon="earth"
                              placeholder="Add countries to block..."
                              @typing="filterBlockoutCountries"
                            >
                              <template #default="props">
                                <strong>{{ props.option.name }}</strong>
                                <span v-if="props.option.note" class="has-text-grey"> - {{ props.option.note }}</span>
                              </template>
                              <template #empty>
                                No countries found
                              </template>
                            </b-taginput>
                          </b-field>
                          <p class="help">
                            Countries blocked for ALL content on this channel (merged with per-media blockouts in EPG export).
                            <br />
                            <strong>Individual countries:</strong> Botswana, Malawi, Mozambique, Namibia, South Africa, Swaziland, Zambia, Zimbabwe, Angola, Ethiopia, Ghana, Kenya, Nigeria, Tanzania, Uganda
                            <br />
                            <strong>Agency:</strong> Blocks all 24 francophone African countries as a group
                          </p>
                        </div>
                      </div>

                      <!-- Public EPG API (pull URL for broadcasters) -->
                      <div class="column is-12">
                        <hr
                          style="margin: 20px 0; border-top: 2px solid #dbdbdb;"
                        />
                        <h3 class="subtitle is-5" style="margin-bottom: 8px;">
                          <span class="icon">
                            <i class="mdi mdi-cloud-download"></i>
                          </span>
                          Public EPG API (Pull URL for Broadcasters)
                        </h3>
                        <p class="help" style="margin-bottom: 15px;">
                          Lets a broadcaster (e.g. EPG ingester) fetch a rolling 7-day-forward EPG XML once a day without logging in.
                          Window auto-refreshes at midnight in the channel timezone.
                          Output is the same MultiChoice synergytoepg XML as the manual export.
                        </p>
                      </div>

                      <div class="column is-3">
                        <div class="control">
                          <b-field label="Public EPG Enabled">
                            <b-switch
                              v-model="item.public_epg_enabled"
                              :disabled="!item.public_epg_api_key_set"
                            >
                              {{ item.public_epg_enabled ? 'Enabled' : 'Disabled' }}
                            </b-switch>
                          </b-field>
                          <p v-if="!item.public_epg_api_key_set" class="help has-text-grey">
                            Generate a key first to enable
                          </p>
                        </div>
                      </div>

                      <div class="column is-3">
                        <div class="control">
                          <b-field label="API Key Status">
                            <b-tag
                              :type="item.public_epg_api_key_set ? 'is-success' : 'is-grey'"
                              size="is-medium"
                            >
                              {{ item.public_epg_api_key_set ? 'Key set' : 'No key' }}
                            </b-tag>
                          </b-field>
                        </div>
                      </div>

                      <div class="column is-6" style="align-self: end;">
                        <b-button
                          type="is-link"
                          icon-left="key-plus"
                          :loading="publicEpgKeyRotating"
                          @click="regeneratePublicEpgKey"
                        >
                          {{ item.public_epg_api_key_set ? 'Rotate Key' : 'Generate Key' }}
                        </b-button>
                        <p class="help has-text-grey-dark" style="margin-top: 6px;">
                          {{ item.public_epg_api_key_set
                            ? 'Rotating invalidates the current key. Existing broadcasters using it will get 404 until you give them the new one.'
                            : 'The key is shown once after generation. We do not store it for later display.'
                          }}
                        </p>
                      </div>

                      <div v-if="publicEpgNewKey" class="column is-12">
                        <div class="notification is-warning">
                          <p><strong>New API key generated. Copy it now — you will not see it again.</strong></p>
                          <div class="field has-addons" style="margin-top: 10px;">
                            <div class="control is-expanded">
                              <b-input :value="publicEpgNewKey" readonly />
                            </div>
                            <div class="control">
                              <b-button
                                icon-left="content-copy"
                                @click="copyToClipboard(publicEpgNewKey, 'API key')"
                              >
                                Copy key
                              </b-button>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div v-if="item.public_epg_api_key_set && item.public_epg_enabled" class="column is-12">
                        <b-field label="Broadcaster Pull URL">
                          <div class="field has-addons" style="width: 100%;">
                            <div class="control is-expanded">
                              <b-input :value="publicEpgUrl" readonly />
                            </div>
                            <div class="control">
                              <b-button
                                icon-left="content-copy"
                                @click="copyToClipboard(publicEpgUrl, 'URL')"
                              >
                                Copy URL
                              </b-button>
                            </div>
                          </div>
                        </b-field>
                        <p class="help">
                          Broadcaster sends: <code>curl -H "X-API-Key: &lt;the-key&gt;" {{ publicEpgUrl }}</code>
                        </p>
                      </div>

                      <!-- VOD Configuration -->
                      <div class="column is-12">
                        <hr
                          style="margin: 20px 0; border-top: 2px solid #dbdbdb;"
                        />
                        <h3 class="subtitle is-5" style="margin-bottom: 15px;">
                          <span class="icon">
                            <i class="mdi mdi-movie-open-play"></i>
                          </span>
                          VOD Configuration (Channel Defaults)
                        </h3>
                        <p class="help" style="margin-bottom: 15px;">
                          Set default VOD settings for all content on this channel. These can be overridden at the media/title level.
                        </p>
                      </div>

                      <div class="column is-3">
                        <div class="control">
                          <b-field label="VOD Enabled by Default">
                            <b-switch v-model="item.vod_enabled_default">
                              {{ item.vod_enabled_default ? 'Enabled' : 'Disabled' }}
                            </b-switch>
                          </b-field>
                        </div>
                      </div>

                      <div class="column is-3">
                        <div class="control">
                          <b-field label="VOD Start Offset (minutes)">
                            <b-input
                              v-model.number="item.vod_start_offset_minutes"
                              type="number"
                              min="0"
                              max="1440"
                              placeholder="0"
                            />
                          </b-field>
                          <p class="help">
                            Minutes after TX time for VOD start (0 = immediately)
                          </p>
                        </div>
                      </div>

                      <div class="column is-3">
                        <div class="control">
                          <b-field label="VOD Expiry Offset (days)">
                            <b-input
                              v-model.number="item.vod_expiry_offset_days"
                              type="number"
                              min="1"
                              max="365"
                              placeholder="7"
                            />
                          </b-field>
                          <p class="help">
                            Days after VOD start for expiry
                          </p>
                        </div>
                      </div>

                      <div class="column is-3">
                        <div class="control">
                          <b-field label="Default VOD Destination">
                            <b-select
                              v-model="item.vod_delivery_destination_id"
                              expanded
                              placeholder="Select destination..."
                            >
                              <option :value="null">None</option>
                              <option
                                v-for="dest in vodDestinations"
                                :key="dest.id"
                                :value="dest.id"
                              >
                                {{ dest.name }}
                              </option>
                            </b-select>
                          </b-field>
                        </div>
                      </div>

                      <!-- Playout Storage Configuration -->
                      <div class="column is-12">
                        <hr
                          style="margin: 20px 0; border-top: 2px solid #dbdbdb;"
                        />
                        <h3 class="subtitle is-5" style="margin-bottom: 15px;">
                          <span class="icon">
                            <i class="mdi mdi-folder-network"></i>
                          </span>
                          Playout Storage Configuration
                        </h3>
                      </div>

                      <div class="column is-12">
                        <div class="control">
                          <ValidationProvider
                            v-slot="{ errors }"
                            name="Playout Storage Root"
                          >
                            <b-field
                              label="Playout Storage Root"
                              :type="{ 'is-danger': errors.length > 0 }"
                              :message="errors[0]"
                            >
                              <b-input
                                v-model="item.playout_storage_root"
                                placeholder="s3://playout-bucket/channel1/ or /nas/playout/channel1/"
                                icon="folder"
                              />
                            </b-field>
                          </ValidationProvider>
                          <p class="help">
                            <strong>Primary</strong> root path for channel
                            playout files (proxy/transcoded files for
                            broadcast). Used in playlist exports (Cinegy CSV,
                            BXF).
                            <br />
                            <strong>Examples:</strong>
                            <br />
                            • S3: <code>s3://playout-bucket/channel1/</code>
                            <br />
                            • NAS: <code>/nas/playout/channel1/</code>
                          </p>
                        </div>

                        <div class="column is-12">
                          <b-field label="Playout Host (Primary)">
                            <b-input
                              v-model="item.playout_host_primary"
                              placeholder="e.g. tenant-onprem-srv01 (hostname of agent managing the primary path)"
                              icon="server-network"
                            />
                          </b-field>
                          <p class="help">
                            Hostname of the Windows playout-stager agent that
                            manages the primary path. The agent self-identifies
                            with this hostname when polling for jobs / reporting
                            disk inventory. Leave blank if no automated agent
                            (operator copies files manually).
                          </p>
                        </div>

                        <div class="column is-12">
                          <hr style="margin: 10px 0; border-top: 1px dashed #dbdbdb;" />
                          <h4 class="subtitle is-6" style="margin-bottom: 10px;">
                            <span class="icon"><i class="mdi mdi-folder-multiple"></i></span>
                            Secondary Playout Target (Optional)
                          </h4>
                          <p class="help" style="margin-bottom: 10px;">
                            Configure a SECOND playout target for this channel
                            (cloud VM, DR site, parallel playout). Leave blank
                            when not needed. The stager and inventory agent will
                            keep this path populated independently from the
                            primary.
                          </p>
                        </div>

                        <div class="column is-12">
                          <b-field label="Playout Storage Root (Secondary)">
                            <b-input
                              v-model="item.playout_storage_root_secondary"
                              placeholder="e.g. E:\URBAN_BREW\DUMISA  or  \\\\dr-site\\share\\channel1\\"
                              icon="folder-multiple"
                            />
                          </b-field>
                          <p class="help">
                            Secondary playout root path. Same format options as
                            primary (SMB UNC, local Windows path, NAS).
                          </p>
                        </div>

                        <div class="column is-12">
                          <b-field label="Playout Host (Secondary)">
                            <b-input
                              v-model="item.playout_host_secondary"
                              placeholder="e.g. CINEGY-VM-A (hostname of agent managing the secondary path)"
                              icon="server-network"
                            />
                          </b-field>
                          <p class="help">
                            Hostname of the Windows playout-stager agent that
                            manages the secondary path.
                          </p>
                        </div>
                      </div>

                      <!-- Folder Structure & Filename Templates -->
                      <div class="column is-12">
                        <hr style="margin: 10px 0; border-top: 1px dashed #dbdbdb;" />
                        <h4 class="subtitle is-6" style="margin-bottom: 10px;">
                          <span class="icon">
                            <i class="mdi mdi-file-tree"></i>
                          </span>
                          Folder Structure & Filename Templates
                        </h4>
                      </div>

                      <div class="column is-12">
                        <div class="control">
                          <b-field label="Use Custom Templates">
                            <b-switch v-model="item.use_custom_templates">
                              {{ item.use_custom_templates ? 'Custom templates enabled' : 'Using system defaults' }}
                            </b-switch>
                          </b-field>
                          <p class="help">
                            Enable custom folder structure and filename patterns for this channel.
                            When disabled, system defaults are used.
                          </p>
                        </div>
                      </div>

                      <template v-if="item.use_custom_templates">
                        <div class="column is-6">
                          <div class="control">
                            <b-field label="Default Supplier Name">
                              <b-input
                                v-model="item.default_supplier_name"
                                placeholder="BMS"
                                icon="account-box"
                              />
                            </b-field>
                            <p class="help">
                              Supplier name used in TX filenames when not specified on media
                            </p>
                          </div>
                        </div>

                        <div class="column is-3">
                          <div class="control">
                            <b-field label="File Extension">
                              <b-select v-model="item.default_file_extension" expanded>
                                <option value="mp4">mp4</option>
                                <option value="mxf">mxf</option>
                                <option value="mov">mov</option>
                              </b-select>
                            </b-field>
                          </div>
                        </div>

                        <div class="column is-3">
                          <div class="control">
                            <b-field label="Uppercase">
                              <div class="field">
                                <b-checkbox v-model="item.uppercase_filenames" style="margin-right: 10px;">
                                  Filenames
                                </b-checkbox>
                                <b-checkbox v-model="item.uppercase_folders">
                                  Folders
                                </b-checkbox>
                              </div>
                            </b-field>
                          </div>
                        </div>

                        <!-- Per-Type File Extensions -->
                        <div class="column is-12">
                          <hr style="margin: 10px 0; border-top: 1px dashed #dbdbdb;" />
                          <h5 class="subtitle is-6" style="margin-bottom: 10px; color: #666;">
                            Per-Type File Extensions (Optional - leave blank to use default above)
                          </h5>
                        </div>

                        <div class="column is-2">
                          <b-field label="Series">
                            <b-select v-model="item.file_extension_series" size="is-small" expanded>
                              <option value="">Default</option>
                              <option value="mp4">mp4</option>
                              <option value="mxf">mxf</option>
                              <option value="mov">mov</option>
                            </b-select>
                          </b-field>
                        </div>
                        <div class="column is-2">
                          <b-field label="Movies">
                            <b-select v-model="item.file_extension_movies" size="is-small" expanded>
                              <option value="">Default</option>
                              <option value="mp4">mp4</option>
                              <option value="mxf">mxf</option>
                              <option value="mov">mov</option>
                            </b-select>
                          </b-field>
                        </div>
                        <div class="column is-2">
                          <b-field label="TVC">
                            <b-select v-model="item.file_extension_tvc" size="is-small" expanded>
                              <option value="">Default</option>
                              <option value="mp4">mp4</option>
                              <option value="mxf">mxf</option>
                              <option value="mov">mov</option>
                            </b-select>
                          </b-field>
                        </div>
                        <div class="column is-2">
                          <b-field label="Promo">
                            <b-select v-model="item.file_extension_promo" size="is-small" expanded>
                              <option value="">Default</option>
                              <option value="mp4">mp4</option>
                              <option value="mxf">mxf</option>
                              <option value="mov">mov</option>
                            </b-select>
                          </b-field>
                        </div>
                        <div class="column is-2">
                          <b-field label="Filler">
                            <b-select v-model="item.file_extension_filler" size="is-small" expanded>
                              <option value="">Default</option>
                              <option value="mp4">mp4</option>
                              <option value="mxf">mxf</option>
                              <option value="mov">mov</option>
                            </b-select>
                          </b-field>
                        </div>
                        <div class="column is-2">
                          <b-field label="GFX">
                            <b-select v-model="item.file_extension_gfx" size="is-small" expanded>
                              <option value="">Default</option>
                              <option value="mp4">mp4</option>
                              <option value="mxf">mxf</option>
                              <option value="mov">mov</option>
                            </b-select>
                          </b-field>
                        </div>

                        <div class="column is-12">
                          <div class="control">
                            <b-field label="Series Folder Template">
                              <b-input
                                v-model="item.folder_template_series"
                                placeholder="TX/SERIES/{TITLE}/{SEASON}"
                                icon="folder-multiple"
                              />
                            </b-field>
                            <p class="help">
                              Placeholders: <code>{TYPE}</code>, <code>{TITLE}</code>, <code>{SEASON}</code>
                              <br />
                              Example: <code>TX/SERIES/{TITLE}/{SEASON}</code> → <code>TX/SERIES/SIYAYA/S14/</code>
                            </p>
                          </div>
                        </div>

                        <div class="column is-12">
                          <div class="control">
                            <b-field label="Non-Series Folder Template (Default)">
                              <b-input
                                v-model="item.folder_template_non_series"
                                placeholder="TX/{TYPE}"
                                icon="folder"
                              />
                            </b-field>
                            <p class="help">
                              Default for non-series types. Placeholders: <code>{TYPE}</code>, <code>{TITLE}</code>
                            </p>
                          </div>
                        </div>

                        <!-- Per-Type Folder Templates -->
                        <div class="column is-12">
                          <hr style="margin: 10px 0; border-top: 1px dashed #dbdbdb;" />
                          <h5 class="subtitle is-6" style="margin-bottom: 10px; color: #666;">
                            Per-Type Folder Templates (Optional - leave blank to use default above)
                          </h5>
                        </div>

                        <div class="column is-4">
                          <b-field label="Movies Folder">
                            <b-input
                              v-model="item.folder_template_movies"
                              placeholder="TX/MOVIES"
                              size="is-small"
                            />
                          </b-field>
                        </div>
                        <div class="column is-4">
                          <b-field label="TVC Folder">
                            <b-input
                              v-model="item.folder_template_tvc"
                              placeholder="TX/TVC"
                              size="is-small"
                            />
                          </b-field>
                        </div>
                        <div class="column is-4">
                          <b-field label="Filler Folder">
                            <b-input
                              v-model="item.folder_template_filler"
                              placeholder="TX/FILLER"
                              size="is-small"
                            />
                          </b-field>
                        </div>
                        <div class="column is-4">
                          <b-field label="Promo Folder">
                            <b-input
                              v-model="item.folder_template_promo"
                              placeholder="TX/PROMO"
                              size="is-small"
                            />
                          </b-field>
                        </div>
                        <div class="column is-4">
                          <b-field label="GFX Folder">
                            <b-input
                              v-model="item.folder_template_gfx"
                              placeholder="TX/GFX"
                              size="is-small"
                            />
                          </b-field>
                        </div>

                        <div class="column is-12">
                          <div class="control">
                            <b-field label="Series Filename Template">
                              <b-input
                                v-model="item.filename_template_series"
                                placeholder="TX_{UID}_{SUPPLIER}_{TITLE}_{SERIES_NO}_{EPISODE_NO}_{RESOLUTION}_{LANGUAGE}_{RATING}.{EXT}"
                                icon="file-video"
                              />
                            </b-field>
                            <p class="help">
                              Placeholders: <code>{UID}</code>, <code>{SUPPLIER}</code>, <code>{TITLE}</code>, <code>{SERIES_NO}</code>, <code>{EPISODE_NO}</code>, <code>{RESOLUTION}</code>, <code>{LANGUAGE}</code>, <code>{RATING}</code>, <code>{EXT}</code>
                              <br />
                              Example: <code>TX_1234_BMS_SIYAYA_14_0001_HD_EN_PG.mp4</code>
                            </p>
                          </div>
                        </div>

                        <div class="column is-12">
                          <div class="control">
                            <b-field label="Non-Series Filename Template (Default)">
                              <b-input
                                v-model="item.filename_template_non_series"
                                placeholder="TX_{UID}_{SUPPLIER}_{TITLE}_{RESOLUTION}_{LANGUAGE}_{RATING}{FLIGHTCODE}.{EXT}"
                                icon="file"
                              />
                            </b-field>
                            <p class="help">
                              Default for non-series types. Placeholders: <code>{UID}</code>, <code>{SUPPLIER}</code>, <code>{TITLE}</code>, <code>{RESOLUTION}</code>, <code>{LANGUAGE}</code>, <code>{RATING}</code>, <code>{FLIGHTCODE}</code>, <code>{EXT}</code>
                            </p>
                          </div>
                        </div>

                        <!-- Per-Type Filename Templates -->
                        <div class="column is-12">
                          <hr style="margin: 10px 0; border-top: 1px dashed #dbdbdb;" />
                          <h5 class="subtitle is-6" style="margin-bottom: 10px; color: #666;">
                            Per-Type Filename Templates (Optional - leave blank to use default above)
                          </h5>
                        </div>

                        <div class="column is-4">
                          <b-field label="Movies Filename">
                            <b-input
                              v-model="item.filename_template_movies"
                              placeholder="TX_{UID}_{TITLE}_{RESOLUTION}.{EXT}"
                              size="is-small"
                            />
                          </b-field>
                        </div>
                        <div class="column is-4">
                          <b-field label="TVC Filename">
                            <b-input
                              v-model="item.filename_template_tvc"
                              placeholder="TX_{UID}_{SUPPLIER}_{TITLE}{FLIGHTCODE}.{EXT}"
                              size="is-small"
                            />
                          </b-field>
                        </div>
                        <div class="column is-4">
                          <b-field label="Filler Filename">
                            <b-input
                              v-model="item.filename_template_filler"
                              placeholder="TX_{UID}_{TITLE}.{EXT}"
                              size="is-small"
                            />
                          </b-field>
                        </div>
                        <div class="column is-4">
                          <b-field label="Promo Filename">
                            <b-input
                              v-model="item.filename_template_promo"
                              placeholder="TX_{UID}_{TITLE}.{EXT}"
                              size="is-small"
                            />
                          </b-field>
                        </div>
                        <div class="column is-4">
                          <b-field label="GFX Filename">
                            <b-input
                              v-model="item.filename_template_gfx"
                              placeholder="TX_{UID}_{TITLE}.{EXT}"
                              size="is-small"
                            />
                          </b-field>
                        </div>
                      </template>

                      <div class="column is-6">
                        <div class="control">
                          <ValidationProvider
                            v-slot="{ errors }"
                            name="Frame Rate (FPS)"
                          >
                            <b-field
                              label="Frame Rate (FPS)"
                              :type="{ 'is-danger': errors.length > 0 }"
                              :message="errors[0]"
                            >
                              <b-select v-model="item.fps" expanded>
                                <option :value="25">25 FPS (PAL)</option>
                                <option :value="29.97">29.97 FPS (NTSC)</option>
                                <option :value="30">30 FPS</option>
                                <option :value="50">50 FPS</option>
                                <option :value="59.94">59.94 FPS</option>
                                <option :value="60">60 FPS</option>
                              </b-select>
                            </b-field>
                          </ValidationProvider>
                          <p class="help">
                            Frame rate for Cinegy playlist timecode calculations
                          </p>
                        </div>
                      </div>

                      <div class="column is-6">
                        <div class="control">
                          <ValidationProvider
                            v-slot="{ errors }"
                            name="Logo Bug Path"
                          >
                            <b-field
                              label="Logo Bug Path"
                              :type="{ 'is-danger': errors.length > 0 }"
                              :message="errors[0]"
                            >
                              <b-input
                                v-model="item.logo_bug_path"
                                placeholder="TX/GFX/logo_bug.png"
                                icon="image"
                              />
                            </b-field>
                          </ValidationProvider>
                          <p class="help">
                            Relative path to channel logo bug/watermark file
                          </p>
                        </div>
                      </div>

                      <div class="column is-12">
                        <div class="control">
                          <ValidationProvider
                            v-slot="{ errors }"
                            name="Opt-Out Block Filler Path"
                          >
                            <b-field
                              label="Opt-Out Block Filler Path"
                              :type="{ 'is-danger': errors.length > 0 }"
                              :message="errors[0]"
                            >
                              <b-input
                                v-model="item.opt_out_filler_path"
                                placeholder="TX/FILLER/opt_out_block.mxf"
                                icon="movie"
                              />
                            </b-field>
                          </ValidationProvider>
                          <p class="help">
                            Relative path to filler video used during opt-out commercial breaks
                          </p>
                        </div>
                      </div>

                      <!-- MCR List Export Configuration -->
                      <div class="column is-12">
                        <hr
                          style="margin: 20px 0; border-top: 2px solid #dbdbdb;"
                        />
                        <h3 class="subtitle is-5" style="margin-bottom: 15px;">
                          <span class="icon">
                            <i class="mdi mdi-playlist-play"></i>
                          </span>
                          MCR List Export Configuration
                        </h3>
                        <p class="help" style="margin-bottom: 15px;">
                          Configure auto-insertion of bumpers and PG rating slates for Cinegy Air Control playlists.
                          Structure: [PG Slate] → [Segment 1] → [Ad Bumper] → [BREAK] → [Ad Bumper] → [Segment 2] → ...
                        </p>
                      </div>

                      <div class="column is-6">
                        <div class="control">
                          <b-field label="Ad Bumper Path">
                            <b-input
                              v-model="item.ad_bumper_path"
                              placeholder="GFX/CHANNEL_GENERIC_AD_BUMPER_10S.mp4"
                              icon="movie-open"
                            />
                          </b-field>
                          <p class="help">
                            Relative path to ad bumper file (from playout storage root). Used before/after commercial breaks.
                          </p>
                        </div>
                      </div>

                      <div class="column is-3">
                        <div class="control">
                          <b-field label="Ad Bumper Duration (sec)">
                            <b-input
                              v-model.number="item.ad_bumper_duration"
                              type="number"
                              min="1"
                              max="60"
                              placeholder="10"
                            />
                          </b-field>
                        </div>
                      </div>

                      <div class="column is-3">
                        <div class="control">
                          <b-field label="Auto-Insert Bumpers">
                            <b-switch v-model="item.auto_insert_bumpers">
                              {{ item.auto_insert_bumpers ? 'Enabled' : 'Disabled' }}
                            </b-switch>
                          </b-field>
                        </div>
                      </div>

                      <div class="column is-6">
                        <div class="control">
                          <b-field label="PG Slate Folder">
                            <b-input
                              v-model="item.pg_slate_folder"
                              placeholder="GFX/PG BOARDS/"
                              icon="folder-image"
                            />
                          </b-field>
                          <p class="help">
                            Folder containing PG rating slate files (relative to playout storage root).
                          </p>
                        </div>
                      </div>

                      <div class="column is-3">
                        <div class="control">
                          <b-field label="PG Slate Duration (sec)">
                            <b-input
                              v-model.number="item.pg_slate_duration"
                              type="number"
                              min="1"
                              max="60"
                              placeholder="10"
                            />
                          </b-field>
                        </div>
                      </div>

                      <div class="column is-3">
                        <div class="control">
                          <b-field label="Auto-Insert PG Slates">
                            <b-switch v-model="item.auto_insert_pg_slates">
                              {{ item.auto_insert_pg_slates ? 'Enabled' : 'Disabled' }}
                            </b-switch>
                          </b-field>
                        </div>
                      </div>

                      <div class="column is-12">
                        <div class="control">
                          <b-field label="PG Slate Video Filename Pattern (Optional)">
                            <b-input
                              v-model="item.pg_slate_pattern"
                              placeholder="RATINGS_{rating}_CHANNEL_2022.mp4"
                              icon="file-video"
                            />
                          </b-field>
                          <p class="help">
                            <strong>Optional:</strong> Pattern for PG slate video files that play before Segment 1. Not all channels use this.
                            <br />
                            Use <code>{rating}</code> as placeholder. Example: <code>RATINGS_{rating}_VIA_2022.mp4</code>
                          </p>
                        </div>
                      </div>

                      <div class="column is-12">
                        <div class="control">
                          <b-field label="Skip PG Slate For These Ratings">
                            <b-taginput
                              v-model="item.pg_slate_skip_ratings"
                              :data="suggestedSkipRatings"
                              autocomplete
                              :allow-new="true"
                              :open-on-focus="true"
                              :before-adding="upperCaseRating"
                              icon="cancel"
                              placeholder="e.g. FAMILY"
                            />
                          </b-field>
                          <p class="help">
                            Ratings listed here SKIP the PG slate at top of show — the Auto Bumper-In plays in its place.
                            Use when the rating is present in EPG metadata but the channel doesn't host a matching slate file
                            on the playout share (Cinegy would otherwise play 10s of dead air).
                            <br />
                            <strong>Note:</strong> The PG rating key overlay is unaffected — it silently no-overlays if the file is missing.
                          </p>
                        </div>
                      </div>

                      <div class="column is-12">
                        <hr style="margin: 10px 0; border-top: 1px dashed #dbdbdb;" />
                        <h4 class="subtitle is-6" style="margin-bottom: 10px;">
                          <span class="icon">
                            <i class="mdi mdi-image-filter-center-focus"></i>
                          </span>
                          PG Rating Key Overlay (Required)
                        </h4>
                      </div>

                      <div class="column is-12">
                        <div class="control">
                          <b-field label="PG Rating Key Pattern">
                            <b-input
                              v-model="item.pg_rating_key_pattern"
                              placeholder="GFX\R_top_right_{rating}.png"
                              icon="image-filter-center-focus"
                            />
                          </b-field>
                          <p class="help">
                            <strong>Required:</strong> Pattern for PG rating key overlay (transparent PNG displayed on screen during playback).
                            <br />
                            Use <code>{rating}</code> as placeholder. The rating is extracted from TX filename (e.g., TX_..._RATING_PG13.mp4).
                            <br />
                            <strong>Default:</strong> <code>GFX\R_top_right_{rating}.png</code> → <code>GFX\R_top_right_PG13.png</code>
                          </p>
                        </div>
                      </div>

                      <!-- Commercial Time Range Configuration -->
                      <div class="column is-12">
                        <hr
                          style="margin: 20px 0; border-top: 2px solid #dbdbdb;"
                        />
                        <h3 class="subtitle is-5" style="margin-bottom: 15px;">
                          <span class="icon">
                            <i class="mdi mdi-clock-outline"></i>
                          </span>
                          Commercial Time Range
                        </h3>
                      </div>

                      <div class="column is-12">
                        <div class="control">
                          <b-field class="has-margin-top-10">
                            <b-checkbox v-model="item.commercials_limit"
                              >Limit Commercial Time Range?</b-checkbox
                            >
                          </b-field>
                        </div>
                      </div>
                      <div v-if="item.commercials_limit" class="column is-6">
                        <div class="control">
                          <BaseTimePicker
                            v-model="item.commercials_start_time"
                            label="Commercials Start Time"
                            :increment-minutes="15"
                            rules="required"
                          />
                        </div>
                      </div>
                      <div v-if="item.commercials_limit" class="column is-6">
                        <div class="control">
                          <BaseTimePicker
                            v-model="item.commercials_end_time"
                            label="Commercials End Time"
                            :increment-minutes="15"
                            rules="required"
                          />
                        </div>
                      </div>
                      <div class="column is-12">
                        <b-field
                          label="Break Schedule STNID Override"
                          :message="breakScheduleStnidMessage"
                          :type="item.break_schedule_stnid ? 'is-info' : ''"
                        >
                          <b-input
                            v-model="item.break_schedule_stnid"
                            placeholder="Leave blank for auto (strip spaces, uppercase)"
                            maxlength="50"
                            :has-counter="false"
                          />
                        </b-field>
                      </div>
                      <div class="column is-12">
                        <p class="is-size-4">
                          <strong>30 Minute Configuration</strong>
                        </p>
                        <draggable
                          v-model="minutes30_segments_config"
                          class="columns is-multiline"
                        >
                          <template
                            v-for="(config, index) of minutes30_segments_config"
                          >
                            <div :key="index" class="column is-2">
                              <div class="control relative">
                                <BaseInput
                                  v-if="
                                    'segment' in config &&
                                      index !==
                                        minutes30_segments_config.length - 1
                                  "
                                  v-model="config.segment"
                                  rules="required"
                                  :label="`Segment duration`"
                                  type="number"
                                  min="0"
                                />
                                <BaseInput
                                  v-if="
                                    'commercial' in config &&
                                      index !==
                                        minutes30_segments_config.length - 1
                                  "
                                  v-model="config.commercial"
                                  rules="required"
                                  :label="`Commercial duration`"
                                  type="number"
                                  min="0"
                                />
                                <b-checkbox
                                  v-if="
                                    'commercial' in config &&
                                      index !==
                                        minutes30_segments_config.length - 1
                                  "
                                  v-model="config.lock_to_slot_end"
                                  size="is-small"
                                  class="has-margin-top-5"
                                  title="When ticked, this commercial is anchored to the END of the 30-min slot regardless of content length. The gap before it becomes available time for shortform."
                                >
                                  <small>Lock to slot end</small>
                                </b-checkbox>
                                <b-field
                                  v-if="
                                    index ===
                                      minutes30_segments_config.length - 1
                                  "
                                  :label="
                                    'segment' in config
                                      ? 'Segment Duration'
                                      : 'Commercial Duration'
                                  "
                                >
                                  <span>Auto</span>
                                </b-field>
                                <a
                                  href=""
                                  style="
                                    position: absolute;
                                    top: 0px;
                                    right: 0px;
                                  "
                                  @click.prevent="remove30minuteItem(index)"
                                  ><b-icon icon="close"
                                /></a>
                              </div>
                            </div>
                          </template>
                        </draggable>
                        <div class="has-margin-top-15 has-margin-left-15">
                          <b-button @click="add30MinSegment('segment')"
                            >Add Segment</b-button
                          >
                          <b-button
                            class="has-margin-left-10"
                            @click="add30MinSegment('commercial')"
                            >Add Commercial</b-button
                          >
                        </div>
                      </div>

                      <div class="column is-12">
                        <p class="is-size-4">
                          <strong>60 Minute Configuration</strong>
                        </p>
                        <draggable
                          v-model="minutes60_segments_config"
                          class="columns is-multiline"
                        >
                          <template
                            v-for="(config, index) of minutes60_segments_config"
                          >
                            <div :key="index" class="column is-2">
                              <div class="control relative">
                                <BaseInput
                                  v-if="
                                    'segment' in config &&
                                      index !==
                                        minutes60_segments_config.length - 1
                                  "
                                  v-model="config.segment"
                                  rules="required"
                                  :label="`Segment duration`"
                                  type="number"
                                  min="0"
                                />
                                <BaseInput
                                  v-if="
                                    'commercial' in config &&
                                      index !==
                                        minutes60_segments_config.length - 1
                                  "
                                  v-model="config.commercial"
                                  rules="required"
                                  :label="`Commercial duration`"
                                  type="number"
                                  min="0"
                                />
                                <b-checkbox
                                  v-if="
                                    'commercial' in config &&
                                      index !==
                                        minutes60_segments_config.length - 1
                                  "
                                  v-model="config.lock_to_slot_end"
                                  size="is-small"
                                  class="has-margin-top-5"
                                  title="When ticked, this commercial is anchored to the END of the 60-min slot regardless of content length. The gap before it becomes available time for shortform."
                                >
                                  <small>Lock to slot end</small>
                                </b-checkbox>
                                <b-field
                                  v-if="
                                    index ===
                                      minutes60_segments_config.length - 1
                                  "
                                  :label="
                                    'segment' in config
                                      ? 'Segment Duration'
                                      : 'Commercial Duration'
                                  "
                                >
                                  <span>Auto</span>
                                </b-field>
                                <a
                                  href=""
                                  style="
                                    position: absolute;
                                    top: 0px;
                                    right: 0px;
                                  "
                                  @click.prevent="remove60minuteItem(index)"
                                  ><b-icon icon="close"
                                /></a>
                              </div>
                            </div>
                          </template>
                        </draggable>
                        <div class="has-margin-top-15 has-margin-left-15">
                          <b-button @click="add60MinSegment('segment')"
                            >Add Segment</b-button
                          >
                          <b-button
                            class="has-margin-left-10"
                            @click="add60MinSegment('commercial')"
                            >Add Commercial</b-button
                          >
                        </div>
                      </div>
                    </div>
                    <div class="has-text-centered">
                      <div class="is-block has-margin-bottom-15">
                        <b-button
                          icon-left="arrow-left"
                          class="is-medium has-margin-right-10"
                          tag="router-link"
                          :to="{
                            name: 'ManageChannels'
                          }"
                        >
                          Back to List</b-button
                        >
                        <button
                          class="button is-primary is-medium btn-align no-lh raised primary-btn"
                          type="submit"
                          :disabled="loading"
                          :class="{ 'is-loading': loading }"
                        >
                          {{ added ? 'Create' : 'Update' }}
                        </button>
                      </div>
                    </div>
                  </form>
                </ValidationObserver>
              </div>
            </div>
          </div>
        </div>
      </CardComponent>
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import draggable from 'vuedraggable'
import { format } from 'date-fns'
import CardComponent from '@/components/CardComponent'
import BaseTimePicker from '@/components/BaseTimePicker'
import BaseSelect from '@/components/BaseSelect'
import ChannelApi from '@/api/channels'
import TxIngestApi from '@/api/txIngest'
import config from '@/config'
/**
 * Format a Date object as a TIME-only string (HH:mm:ss).
 * Used for TIME columns in database (not DATETIME).
 */
function formatTimeOnly(date) {
  if (!date) return null
  if (typeof date === 'string') {
    // Already a string - check if it's time-only format
    if (/^\d{2}:\d{2}(:\d{2})?$/.test(date)) {
      return date // Already in HH:mm or HH:mm:ss format
    }
    // Parse string to Date first
    date = new Date(date)
  }
  if (!(date instanceof Date) || isNaN(date)) return null
  return format(date, 'HH:mm:ss')
}

/**
 * Parse a TIME-only string to a Date object with today's date.
 * Used for TIME columns when loading from database.
 * CRITICAL: Uses local time interpretation, not UTC.
 */
function parseTimeOnly(timeString) {
  if (!timeString) return null
  if (timeString instanceof Date) return timeString // Already a Date

  // If it's a time-only string like "06:00:00", create a Date with today's date
  if (typeof timeString === 'string' && /^\d{2}:\d{2}(:\d{2})?$/.test(timeString)) {
    const [hours, minutes, seconds] = timeString.split(':')
    const date = new Date()
    date.setHours(parseInt(hours, 10))
    date.setMinutes(parseInt(minutes, 10))
    date.setSeconds(seconds ? parseInt(seconds, 10) : 0)
    date.setMilliseconds(0)
    return date
  }

  // Otherwise parse as full date/time string
  return new Date(timeString)
}

export default {
  name: 'EditChannel',
  components: {
    CardComponent,
    BaseTimePicker,
    draggable,
    BaseSelect
  },
  metaInfo: {
    title: 'Edit Channel'
  },
  computed: {
    ...mapGetters(['user']),
    breakScheduleStnidMessage() {
      const override = (this.item.break_schedule_stnid || '').trim()
      if (override) {
        return `Break schedule export will use "${override}" verbatim.`
      }
      const name = this.item.name || ''
      const auto = name.replace(/\s+/g, '').toUpperCase()
      return auto
        ? `Auto: "${auto}". Set this if the broadcaster requires a different value (e.g. preserving spaces).`
        : 'Auto: derived from channel name (spaces stripped, uppercased).'
    },
    publicEpgUrl() {
      // Backend base URL, taken from config so the URL we hand to a broadcaster
      // points at the right environment (prod vs staging).
      const env = process.env.NODE_ENV === 'development' ? 'development' : 'production'
      const base = (config.environment[env] && config.environment[env].apiUrl) || ''
      const channelId = this.item && this.item.id
      if (!base || !channelId) return ''
      return `${base.replace(/\/$/, '')}/api/public/epg/${channelId}.xml`
    }
  },
  async mounted() {
    this.added = this.$route.name === 'CreateChannel' ? true : false
    // Initialize filtered blockout countries list
    this.filteredBlockoutCountries = this.blockoutCountries
    // Load VOD destinations for dropdown
    try {
      const destinations = await TxIngestApi.getVodDestinations()
      this.vodDestinations = destinations || []
    } catch (err) {
      console.warn('Could not load VOD destinations:', err)
      this.vodDestinations = []
    }
    if (!this.added) {
      await this.setData()
    }
    this.initialized = true
  },
  data() {
    return {
      loading: false,
      initialized: false,
      added: false,
      minutes30_segments_config: [],
      minutes60_segments_config: [],
      item: {
        // CRITICAL: Initialize TIME fields as null, not Date objects
        // TIME columns should be "HH:mm:ss" strings, not full timestamps
        commercials_start_time: null,
        commercials_end_time: null,
        break_schedule_stnid: '',
        // Multi-target playout (Phase 1, additive — inert until inventory agent + stager rewire ship)
        playout_storage_root_secondary: '',
        playout_host_primary: '',
        playout_host_secondary: '',
        timezone: 'Africa/Johannesburg', // Default timezone
        fps: 25, // Default to 25 FPS (PAL)
        logo_bug_path: '',
        opt_out_filler_path: '',
        // MCR List Export fields
        ad_bumper_path: '',
        ad_bumper_duration: 10,
        auto_insert_bumpers: true,
        pg_slate_folder: '',
        pg_slate_pattern: '',
        pg_slate_duration: 10,
        pg_slate_skip_ratings: [],
        auto_insert_pg_slates: true,
        pg_rating_key_pattern: '',
        // Folder structure and filename template fields
        use_custom_templates: false,
        folder_template_series: 'TX/SERIES/{TITLE}/{SEASON}',
        folder_template_non_series: 'TX/{TYPE}',
        filename_template_series: 'TX_{UID}_{SUPPLIER}_{TITLE}_{SERIES_NO}_{EPISODE_NO}_{RESOLUTION}_{LANGUAGE}_{RATING}.{EXT}',
        filename_template_non_series: 'TX_{UID}_{SUPPLIER}_{TITLE}_{RESOLUTION}_{LANGUAGE}_{RATING}{FLIGHTCODE}.{EXT}',
        // Per-type folder templates (optional - falls back to folder_template_non_series)
        folder_template_movies: '',
        folder_template_tvc: '',
        folder_template_filler: '',
        folder_template_promo: '',
        folder_template_gfx: '',
        // Per-type filename templates (optional - falls back to filename_template_non_series)
        filename_template_movies: '',
        filename_template_tvc: '',
        filename_template_filler: '',
        filename_template_promo: '',
        filename_template_gfx: '',
        default_file_extension: 'mp4',
        // Per-type file extensions (leave blank to use default_file_extension)
        file_extension_series: '',
        file_extension_movies: '',
        file_extension_tvc: '',
        file_extension_promo: '',
        file_extension_filler: '',
        file_extension_gfx: '',
        default_supplier_name: 'BMS',
        uppercase_filenames: true,
        uppercase_folders: true,
        // EPG Configuration
        epg_default_blockouts: [],
        // Public EPG API (pull endpoint for broadcasters)
        public_epg_enabled: false,
        public_epg_api_key_set: false,
        // VOD Configuration
        vod_enabled_default: false,
        vod_start_offset_minutes: 0,
        vod_expiry_offset_days: 7,
        vod_delivery_destination_id: null
      },
      // Public EPG state (UI only — not persisted, not sent to backend)
      publicEpgKeyRotating: false,
      publicEpgNewKey: '',
      // VOD Destinations (loaded from API)
      vodDestinations: [],
      // MultiChoice EPG country blockout options
      blockoutCountries: [
        { name: 'Agency', note: 'All 24 francophone African countries' },
        { name: 'Angola', note: '' },
        { name: 'Botswana', note: '' },
        { name: 'Ethiopia', note: '' },
        { name: 'Ghana', note: '' },
        { name: 'Kenya', note: '' },
        { name: 'Malawi', note: '' },
        { name: 'Mozambique', note: '' },
        { name: 'Namibia', note: '' },
        { name: 'Nigeria', note: '' },
        { name: 'South Africa', note: '' },
        { name: 'Swaziland', note: '' },
        { name: 'Tanzania', note: '' },
        { name: 'Uganda', note: '' },
        { name: 'Zambia', note: '' },
        { name: 'Zimbabwe', note: '' }
      ],
      filteredBlockoutCountries: [],
      // Common ratings shown as autocomplete suggestions for the slate skip list.
      // allow-new=true so operators can still type any value the channel uses —
      // this list is just to nudge consistency with what getAgeRestriction reads.
      suggestedSkipRatings: ['FAMILY', 'G', 'A', 'ALL', 'U', 'PG', 'PG13', '13', '16', '18']
    }
  },
  methods: {
    ...mapActions(['getUser']),
    // Normalise rating tags to uppercase as the operator adds them, so the array
    // we POST matches what api/exportCinegy.js compares against (it upper-cases
    // both sides defensively, but this keeps the stored value clean).
    upperCaseRating(tag) {
      return String(tag || '').toUpperCase().trim()
    },
    async regeneratePublicEpgKey() {
      if (!this.item.id) {
        this.$buefy.toast.open({ message: 'Save the channel first before generating a key.', type: 'is-warning', duration: 4000 })
        return
      }
      if (this.item.public_epg_api_key_set) {
        const ok = await this.$buefy.dialog.confirm({
          title: 'Rotate Public EPG API key?',
          message: 'The existing key will stop working immediately. Any broadcaster using it will get 404 until you send them the new one. Continue?',
          confirmText: 'Rotate',
          type: 'is-warning',
          hasIcon: true
        }).then(() => true).catch(() => false)
        if (!ok) return
      }
      this.publicEpgKeyRotating = true
      try {
        const res = await ChannelApi.regeneratePublicEpgKey(this.item.id)
        if (res && res.apiKey) {
          this.publicEpgNewKey = res.apiKey
          this.item.public_epg_api_key_set = true
          this.$buefy.toast.open({ message: 'API key generated — copy it now.', type: 'is-success', duration: 4000 })
        } else {
          this.$buefy.toast.open({ message: 'Key generation returned no key.', type: 'is-danger', duration: 4000 })
        }
      } catch (err) {
        const msg = (err && err.data && err.data.err) || (err && err.statusText) || 'Key generation failed'
        this.$buefy.toast.open({ message: msg, type: 'is-danger', duration: 5000 })
      } finally {
        this.publicEpgKeyRotating = false
      }
    },
    async copyToClipboard(text, label) {
      try {
        await navigator.clipboard.writeText(text)
        this.$buefy.toast.open({ message: `${label || 'Value'} copied`, type: 'is-success', duration: 2000 })
      } catch (e) {
        this.$buefy.toast.open({ message: `Copy failed — select and copy manually`, type: 'is-warning', duration: 3000 })
      }
    },
    filterBlockoutCountries(text) {
      this.filteredBlockoutCountries = this.blockoutCountries.filter((country) => {
        return country.name.toLowerCase().indexOf(text.toLowerCase()) >= 0
      })
    },
    remove30minuteItem(index) {
      this.minutes30_segments_config.splice(index, 1)
    },
    add30MinSegment(type) {
      if (type === 'segment') {
        this.minutes30_segments_config.push({ segment: '0' })
      } else {
        // Initialize lock_to_slot_end so the b-checkbox is reactive in Vue 2
        this.minutes30_segments_config.push({ commercial: '0', lock_to_slot_end: false })
      }
    },
    remove60minuteItem(index) {
      this.minutes60_segments_config.splice(index, 1)
    },
    add60MinSegment(type) {
      if (type === 'segment') {
        this.minutes60_segments_config.push({ segment: '0' })
      } else {
        // Initialize lock_to_slot_end so the b-checkbox is reactive in Vue 2
        this.minutes60_segments_config.push({ commercial: '0', lock_to_slot_end: false })
      }
    },
    async setData(id) {
      this.initialized = false
      this.added = false
      let item = await ChannelApi.getById(id || this.$route.params.id)
      if (item) {
        this.item.id = item.id
        this.item.name = item.name
        // CRITICAL: Use parseTimeOnly for TIME columns (not DATETIME)
        // This prevents UTC interpretation which would shift the time by timezone offset
        this.item.start_time = item.start_time
          ? parseTimeOnly(item.start_time)
          : null
        this.item.commercials_start_time = item.commercials_start_time
          ? parseTimeOnly(item.commercials_start_time)
          : null
        this.item.commercials_end_time = item.commercials_end_time
          ? parseTimeOnly(item.commercials_end_time)
          : null
        this.item.timezone = item.timezone || 'Africa/Johannesburg'
        this.item.commercials_limit = Boolean(item.commercials_limit)
        this.item.break_schedule_stnid = item.break_schedule_stnid || ''
        this.item.playout_storage_root = item.playout_storage_root || ''
        this.item.playout_storage_root_secondary = item.playout_storage_root_secondary || ''
        this.item.playout_host_primary = item.playout_host_primary || ''
        this.item.playout_host_secondary = item.playout_host_secondary || ''
        this.item.fps = item.frame_rate || 25
        this.item.logo_bug_path = item.logo_bug_path || ''
        this.item.opt_out_filler_path = item.commercial_break_filler_path || ''
        // MCR List Export fields
        this.item.ad_bumper_path = item.ad_bumper_path || ''
        this.item.ad_bumper_duration = item.ad_bumper_duration || 10
        // CRITICAL: MySQL returns 0/1 for boolean, not true/false
        // Using Boolean() converts 0 to false and 1 to true correctly
        this.item.auto_insert_bumpers = Boolean(item.auto_insert_bumpers)
        this.item.pg_slate_folder = item.pg_slate_folder || ''
        this.item.pg_slate_pattern = item.pg_slate_pattern || ''
        this.item.pg_slate_duration = item.pg_slate_duration || 10
        // pg_slate_skip_ratings is a JSON column — knex may return it parsed (array)
        // or as a raw JSON string. Normalise to an uppercase string array for the
        // b-taginput, defaulting to [] when NULL (no ratings skipped).
        if (item.pg_slate_skip_ratings) {
          let skip = item.pg_slate_skip_ratings
          if (typeof skip === 'string') {
            try { skip = JSON.parse(skip) } catch (e) { skip = [] }
          }
          this.item.pg_slate_skip_ratings = Array.isArray(skip)
            ? skip.map(r => String(r).toUpperCase().trim()).filter(Boolean)
            : []
        } else {
          this.item.pg_slate_skip_ratings = []
        }
        // CRITICAL: MySQL returns 0/1 for boolean, not true/false
        this.item.auto_insert_pg_slates = Boolean(item.auto_insert_pg_slates)
        this.item.pg_rating_key_pattern = item.pg_rating_key_pattern || ''
        // Folder structure and filename template fields
        this.item.use_custom_templates = Boolean(item.use_custom_templates)
        this.item.folder_template_series = item.folder_template_series || 'TX/SERIES/{TITLE}/{SEASON}'
        this.item.folder_template_non_series = item.folder_template_non_series || 'TX/{TYPE}'
        this.item.filename_template_series = item.filename_template_series || 'TX_{UID}_{SUPPLIER}_{TITLE}_{SERIES_NO}_{EPISODE_NO}_{RESOLUTION}_{LANGUAGE}_{RATING}.{EXT}'
        this.item.filename_template_non_series = item.filename_template_non_series || 'TX_{UID}_{SUPPLIER}_{TITLE}_{RESOLUTION}_{LANGUAGE}_{RATING}{FLIGHTCODE}.{EXT}'
        // Per-type folder templates
        this.item.folder_template_movies = item.folder_template_movies || ''
        this.item.folder_template_tvc = item.folder_template_tvc || ''
        this.item.folder_template_filler = item.folder_template_filler || ''
        this.item.folder_template_promo = item.folder_template_promo || ''
        this.item.folder_template_gfx = item.folder_template_gfx || ''
        // Per-type filename templates
        this.item.filename_template_movies = item.filename_template_movies || ''
        this.item.filename_template_tvc = item.filename_template_tvc || ''
        this.item.filename_template_filler = item.filename_template_filler || ''
        this.item.filename_template_promo = item.filename_template_promo || ''
        this.item.filename_template_gfx = item.filename_template_gfx || ''
        this.item.default_file_extension = item.default_file_extension || 'mp4'
        // Per-type file extensions
        this.item.file_extension_series = item.file_extension_series || ''
        this.item.file_extension_movies = item.file_extension_movies || ''
        this.item.file_extension_tvc = item.file_extension_tvc || ''
        this.item.file_extension_promo = item.file_extension_promo || ''
        this.item.file_extension_filler = item.file_extension_filler || ''
        this.item.file_extension_gfx = item.file_extension_gfx || ''
        this.item.default_supplier_name = item.default_supplier_name || 'BMS'
        // CRITICAL: MySQL returns 0/1 for boolean, not true/false
        this.item.uppercase_filenames = Boolean(item.uppercase_filenames)
        this.item.uppercase_folders = Boolean(item.uppercase_folders)
        // EPG Configuration - parse JSON array from database
        if (item.epg_default_blockouts) {
          const blockouts = typeof item.epg_default_blockouts === 'string'
            ? JSON.parse(item.epg_default_blockouts)
            : item.epg_default_blockouts
          // Convert string array to objects for taginput component
          this.item.epg_default_blockouts = blockouts.map(name => {
            return this.blockoutCountries.find(c => c.name === name) || { name: name, note: '' }
          })
        } else {
          this.item.epg_default_blockouts = []
        }
        // Initialize filtered list
        this.filteredBlockoutCountries = this.blockoutCountries
        // Public EPG API (key itself never returned by backend - only the boolean)
        this.item.public_epg_enabled = Boolean(item.public_epg_enabled)
        this.item.public_epg_api_key_set = Boolean(item.public_epg_api_key_set)
        // VOD Configuration
        this.item.vod_enabled_default = Boolean(item.vod_enabled_default)
        this.item.vod_start_offset_minutes = item.vod_start_offset_minutes ?? 0
        this.item.vod_expiry_offset_days = item.vod_expiry_offset_days ?? 7
        this.item.vod_delivery_destination_id = item.vod_delivery_destination_id || null
        // Normalize commercial entries so lock_to_slot_end is always present
        // (Vue 2 reactivity: properties added later via v-model on b-checkbox would
        // not be reactive unless declared up front)
        const normalizeConfig = (entries) => {
          return entries.map(entry => {
            if (entry && 'commercial' in entry && !('lock_to_slot_end' in entry)) {
              return { ...entry, lock_to_slot_end: false }
            }
            return entry
          })
        }
        if (item.minutes30_segments_config) {
          this.minutes30_segments_config = normalizeConfig(
            JSON.parse(item.minutes30_segments_config)
          )
        }
        if (item.minutes60_segments_config) {
          this.minutes60_segments_config = normalizeConfig(
            JSON.parse(item.minutes60_segments_config)
          )
        }
      }
      this.initialized = true
    },
    async submit() {
      this.loading = true
      this.item.minutes30_segments_config = this.minutes30_segments_config
      this.item.minutes60_segments_config = this.minutes60_segments_config

      // CRITICAL: Use formatTimeOnly for ALL TIME columns (not formatLocalDateTime)
      // TIME columns should be "HH:mm:ss" strings, not full ISO datetime
      // This prevents UTC interpretation which would shift the time by timezone offset
      if (this.item.start_time) {
        this.item.start_time = formatTimeOnly(this.item.start_time)
      }
      if (this.item.commercials_start_time) {
        this.item.commercials_start_time = formatTimeOnly(this.item.commercials_start_time)
      }
      if (this.item.commercials_end_time) {
        this.item.commercials_end_time = formatTimeOnly(this.item.commercials_end_time)
      }

      // EPG Configuration - convert objects back to string array for database
      if (this.item.epg_default_blockouts && this.item.epg_default_blockouts.length > 0) {
        this.item.epg_default_blockouts = this.item.epg_default_blockouts.map(c => c.name || c)
      } else {
        this.item.epg_default_blockouts = null
      }

      if (!this.item.id) {
        try {
          let response = await ChannelApi.create(this.item)
          this.$buefy.toast.open({
            message: 'Channel created successfully!',
            position: 'is-top',
            type: 'is-success'
          })
          this.$router.push({
            name: 'EditChannel',
            params: { id: response.channelId }
          })
          await this.setData(response.channelId)
          this.loading = false
        } catch (err) {
          console.log(err)
          this.$buefy.toast.open({
            message: 'Error saving channel',
            position: 'is-top',
            type: 'is-danger'
          })
          this.loading = false
        }
      } else {
        try {
          this.item.channelId = this.item.id
          await ChannelApi.update(this.item)
          this.$buefy.toast.open({
            message: 'Channel updated successfully!',
            position: 'is-top',
            type: 'is-success'
          })
          await this.setData()
          this.loading = false
        } catch (err) {
          console.log(err)
          this.$buefy.toast.open({
            message: 'Error saving channel',
            position: 'is-top',
            type: 'is-danger'
          })
          this.loading = false
        }
      }
    }
  }
}
</script>
